import { useEffect, useRef, useState } from "react";
import MainLayout from "../../layout/MainLayout";
import { actionPost, getApi, useApiCall } from "../../services/api";
import useExpiredToken from "../../utils/useExpiredToken";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useToast } from "../../utils/Toast";
import { processEncryptedResponse } from "../../utils/ResponseDecreypt";
import TabsBtn from "../../common/TabsBtn";
import moment from "moment";
import { dropdownQuestionIds } from "../../constant";
import audioFile from "./file_example_MP3_700KB.mp3";
import { formatDate } from "../../utils/DateFormat";

const ztpTitle = "ZTP Parameters";

const AuditDetails = () => {
  const { callApi } = useApiCall();
  const toast = useToast();
  const audioRef = useRef(null);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [data, setData] = useState({});
  const [queations, setQueastion] = useState([]);
  const [categories, setCategories] = useState([]);
  const [dispositions, setDispositions] = useState([]);
  const [subDispositions, setSubDispositions] = useState([]);
  const token = useSelector((state) => state.auth.jwtToken);
  const userId = useSelector((state) => state.auth.userId);
  const { fname: user_fname } = useSelector((state) => state.auth);
  const expireToken = useExpiredToken();
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [answers, setAnswers] = useState({});
  const { auditType } = location.state || {};
  const [currentCategoryIndex, setCurrentCategoryIndex] = useState(0);
  const [actualCurrentCategoryIndex, setActualCurrentCategoryIndex] = useState(0);
  const [responses, setResponses] = useState({});
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [hasSubmit, setHasSubmit] = useState(false);
  const [remarks, setRemarks] = useState(undefined);

  const [selectedOption, setSelectedOption] = useState(null);

  let ztpCategoryId = 0;
  const groupedByCategory = queations.reduce((acc, question) => {
    const { categoryId, category } = question;
    if (category === ztpTitle && !ztpCategoryId) ztpCategoryId = categoryId;
    if (!acc[categoryId]) {
      acc[categoryId] = { title: category, questions: [] };
    }
    acc[categoryId].questions.push(question);
    return acc;
  }, {});

  const categoryIds = Object.keys(groupedByCategory);
  const currentCategory = groupedByCategory[categoryIds[currentCategoryIndex]];

  const validateAnswers = () => {
    let missing = [];

    if ((auditType === 1 || auditType === "1") && currentCategory.title !== ztpTitle) {
      currentCategory.questions.forEach((q) => {
        const val = responses[q.id];
        if (val == null || val === "" || (typeof val === "number" && Number.isNaN(val))) {
          missing.push(q.parameter);
        }
      });
    } else if ((auditType === 1 || auditType === "1") && selectedOption == null) {
      missing.push("ZTP Parameters");
    } else {
      queations.forEach((q) => {
        const val = answers[q.id];
        if (val == null || val === "" || (typeof val === "number" && Number.isNaN(val))) {
          missing.push(q.question);
        }
      });
    }

    // special case for category/disposition/sub-disposition
    if (auditType !== 1 && auditType !== "1") {
      if (!answers["category"]) missing.push("Call Category");
      if (!answers["disposition"] && dispositions.length > 0) missing.push("Disposition");
      if (!answers["subDisposition"] && subDispositions.length > 0) missing.push("Sub Disposition");
    }

    return missing;
  };

  const handlePrevQue = () => {
    if (currentCategoryIndex > 0) {
      setCurrentCategoryIndex((prev) => prev - 1);
    }
  };

  const handleNextQue = () => {
    setHasSubmit(true);
    const missing = validateAnswers();
    if (missing.length > 0) {
      toast.error("Please answer all required questions");
      return;
    }

    if (currentCategoryIndex < categoryIds.length - 1) {
      const nextIndex = currentCategoryIndex + 1;

      setCurrentCategoryIndex(nextIndex);
      setHasSubmit(false);

      setActualCurrentCategoryIndex((prev) => Math.max(prev, nextIndex));
    }
  };

  const handleAnswerChange = (questionId, value) => {
    setAnswers((prevAnswers) => ({
      ...prevAnswers,
      [questionId]: value,
    }));
  };

  const handleResponseChange = (questionId, value) => {
    setResponses((prevResponses) => ({
      ...prevResponses,
      [questionId]: value,
    }));
    currentCategory.questions.find((q) => q.id === questionId).score = value;
  };

  const fetchDetails = async () => {
    let res;
    if (auditType == "1") {
      res = await getApi(`call-audits/1/categories`, token, expireToken);
    } else {
      res = await getApi(`call-audits/${auditType}/questions`, token, expireToken);
    }

    if (res.status) {
      const sortedQuestions = res.data.sort((a, b) => a.id - b.id);
      setQueastion(sortedQuestions);
      if (auditType == "1") {
        const initialResponses = {};
        res.data.forEach((question) => {
          initialResponses[question.id] = question.markOption1;
        });
        setResponses(initialResponses);
      }
    }
  };

  const fetchAuditDetails = async () => {
    const res = await actionPost("calls/summary", token, "READ_BY_ID", { id: id }, expireToken);
    const data = await processEncryptedResponse(res);

    if (data) {
      setData(data);
    }
  };

  const auditSubmit = async () => {
    setHasSubmit(true);
    const missing = validateAnswers();
    if (missing?.filter(Boolean).length > 0) {
      toast.error("Please answer all required questions");
      return;
    }

    let auditData;
    let commonData = {
      callId: id,
      auditType: auditType,
      empId: "EM12",
      agentName: data?.agent_name,
      callDate: data?.created_at
        ? moment(data?.created_at).format("YYYY-MM-DD")
        : moment(data?.updated_at).format("YYYY-MM-DD"),
      auditDate: moment().format("YYYY-MM-DD"),
      mobileNumber: data?.phone_number,
      duration: data?.duration,
      recPath: data?.recording_url,
      center: data?.circle,
      callType: data?.call_type,
      hangUpBy: data?.disconnect_by,
      callObservation: answers["7"],
      fatalNonfatalId: answers["9"],
      fatalReason: answers["fatalReason"] || "None",
    };
    if (auditType == "1") {
      auditData = { ...commonData };
      const evaluation = [];

      Object.entries(groupedByCategory).forEach(([categoryId, category]) => {
        if (category.title === ztpTitle) {
          const answeredZtp = category.questions?.find((q) => q.id === Number(selectedOption));
          if (!answeredZtp) {
            evaluation.push({
              categoryId: Number(categoryId),
              categoryName: category.title,
              totalWeightage: 0,
              obtainedScore: -1,
              questions: [],
            });
          } else {
            evaluation.push({
              categoryId: Number(categoryId),
              categoryName: category.title,
              totalWeightage: category.questions.reduce((sum, q) => sum + (q.weightage || 0), 0),
              obtainedScore: 0,
              questions: [
                {
                  questionId: answeredZtp.id,
                  question: answeredZtp.parameter,
                  weightage: answeredZtp.weightage,
                  score: answeredZtp.score || 0,
                  fatal: answeredZtp.fatal,
                  risk: answeredZtp.riskSegment,
                },
              ],
            });
          }
        } else {
          const fatalQuestion = category.questions.find((q) => q.fatal === 1);

          evaluation.push({
            categoryId: Number(categoryId),
            categoryName: category.title,
            totalWeightage: category.questions.reduce((sum, q) => sum + (q.weightage || 0), 0),
            obtainedScore: category.questions.reduce((sum, q) => {
              if (q.fatal === 1 && q.score === -1) return sum;
              return sum + (fatalQuestion?.score === 0 ? 0 : q.score || 0);
            }, 0),
            questions: category.questions.map((question) => ({
              questionId: question.id,
              question: question.parameter,
              weightage: question.weightage,
              score: fatalQuestion?.score === 0 ? 0 : question.score,
              fatal: question.fatal,
              risk: question.riskSegment,
            })),
          });
        }
      });

      auditData.evaluation = evaluation;
      auditData.remarks = remarks;
    } else {
      auditData = {
        ...commonData,
        dispositionAccuracyId: answers["1"] || answers["18"] || answers["23"],
        actualDispositionId: answers["1"] || answers["18"] || answers["23"],
        acptId: answers["2"],
        customerVocId: answers["3"],
        l1Id: answers["4"],
        l2Id: answers["5"],
        l3Id: answers["6"],
        feedbackId: answers["8"] || answers["22"] || answers["26"],
        fatalNonfatalId: answers["9"],
        disposition_id: answers["disposition"],
        sub_disposition_id: answers["subDisposition"],
        ncCall: answers["20"] === 1 ? true : false,
        remarks: answers["21"] || answers["25"] || "None",
        rfd_identified_id: answers["27"] ? Number(answers["27"]) : null,
        probing_id: answers["28"] ? Number(answers["28"]) : null,
        negotiation_id: answers["29"] ? Number(answers["29"]) : null,
        urgency_id: answers["30"] ? Number(answers["30"]) : null,
        rebuttals_id: answers["31"] ? Number(answers["31"]) : null,
        on_call_attempt_id: answers["32"] ? Number(answers["32"]) : null,
        soft_skills_issue_id: answers["33"] ? Number(answers["33"]) : null,
        casual_choice_of_words_identified_id: answers["34"] ? Number(answers["34"]) : null,
        customer_behaviour_id: answers["35"] ? Number(answers["35"]) : null,
        call_quality_rag_id: answers["36"] ? Number(answers["36"]) : null,
        aoi_id: answers["37"] ? answers["37"] : null,
        payment_expected_date: answers["38"] ? answers["38"] : null,
      };
    }

    console.log("Submitting auditData:", auditData);

    const { status } = await callApi({
      endpoint: "call-audits/add-call-audits",
      bodyData: auditData,
      apiMethod: "post",
    });

    if (status) {
      setTimeout(() => {
        navigate("/assigned-calls");
      }, 200);
    }
  };

  // ✅ Correct file paths for public assets
  const playlist = audioFile;

  const togglePlayback = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      audio
        .play()
        .then(() => {
          setIsPlaying(true);
        })
        .catch((error) => {
          toast.error(error.message);
        });
    }
  };

  const skipTime = (seconds) => {
    const audio = audioRef.current;
    if (audio) {
      audio.currentTime = Math.min(Math.max(audio.currentTime + seconds, 0), audio.duration);
    }
  };

  const handleTimeUpdate = () => {
    const audio = audioRef.current;
    if (audio) {
      setCurrentTime(audio.currentTime);
      setProgress((audio.currentTime / audio.duration) * 100);
    }
  };

  const handleLoadedMetadata = () => {
    const audio = audioRef.current;
    if (audio) setDuration(audio.duration);
  };

  const formatTime = (secs) => {
    if (isNaN(secs)) return "0:00";
    const minutes = Math.floor(secs / 60);
    const seconds = Math.floor(secs % 60)
      .toString()
      .padStart(2, "0");
    return `${minutes}:${seconds}`;
  };

  const handleProgressClick = (e) => {
    const audio = audioRef.current;
    const bar = e.target;
    const rect = bar.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const newTime = (clickX / rect.width) * audio.duration;
    audio.currentTime = newTime;
  };

  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.load();
      if (isPlaying) {
        audio.play();
      }
    }
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    const handleEnded = () => {
      setIsPlaying(false);
    };
    if (audio) {
      audio.addEventListener("ended", handleEnded);
    }
    return () => {
      if (audio) {
        audio.removeEventListener("ended", handleEnded);
      }
    };
  }, []);

  const fetchCategories = async () => {
    if (auditType === 1) return;
    const { response, status } = await callApi({
      endpoint: `dispositions/categories`,
      showToast: false,
    });
    if (status) {
      setCategories(response.data?.data);
    }
  };

  useEffect(() => {
    fetchAuditDetails();
    fetchDetails();
    fetchCategories();
  }, []);

  const fetchDispositions = async () => {
    if (!answers["category"]) return;
    const { response, status } = await callApi({
      endpoint: `dispositions/category/${answers["category"]}/dispositions`,
      showToast: false,
    });
    if (status) {
      handleAnswerChange("disposition", undefined);
      setDispositions(response.data?.data);
    }
  };

  useEffect(() => {
    fetchDispositions();
  }, [answers["category"]]);

  const fetchSubDispositions = async () => {
    if (!answers["disposition"]) return;
    const { response, status } = await callApi({
      endpoint: `dispositions/disposition/${answers["disposition"]}/sub-dispositions`,
      showToast: false,
    });
    if (status) {
      handleAnswerChange("subDisposition", undefined);
      setSubDispositions(response.data?.data);
    }
  };

  useEffect(() => {
    fetchSubDispositions();
  }, [answers["disposition"]]);

  const getOptions = (q) =>
    q.markOption1 !== q.markOption2
      ? [
          { label: q.markOption1 === -1 ? "N/A" : q.markOption1, value: q.markOption1 },
          { label: q.markOption2, value: q.markOption2 },
        ]
      : [{ label: q.markOption1 === -1 ? "N/A" : q.markOption1, value: q.markOption1 }];

  const questionComponent = (que, extra) => (
    <>
      <label className={`form-label ${!answers[que.id] && hasSubmit ? "text-danger fw-bold" : ""}`}>
        {que?.question}
        {que?.question?.endsWith("?") || que?.question?.endsWith(".") ? "" : "?"}
      </label>

      <div className="my-1">
        {Array.isArray(que?.options) && que.options.length > 0 ? (
          dropdownQuestionIds[auditType].includes(que.id) ? (
            <select
              className={`form-select`}
              style={{ fontSize: "14px" }}
              value={answers[que.id] || ""}
              onChange={(e) => handleAnswerChange(que.id, Number(e.target.value))}
            >
              <option value="" disabled>
                Select an option
              </option>
              {que.options.map((opt) => (
                <option key={opt.id} value={Number(opt.id)}>
                  {opt.text || opt.name}
                </option>
              ))}
            </select>
          ) : (
            que.options.map((opt) => (
              <div className="form-check form-check-inline" key={opt.id}>
                <input
                  className={`form-check-input`}
                  type="radio"
                  name={`question-${que.id}`}
                  id={`question-${que.id}-option-${opt.id}`}
                  value={Number(opt.id)}
                  checked={answers[que.id] === opt.id}
                  onChange={() => handleAnswerChange(que.id, Number(opt.id))}
                />
                <label
                  className="form-check-label"
                  htmlFor={`question-${que.id}-option-${opt.id}`}
                  style={{ fontWeight: 500 }}
                >
                  {opt.text || opt.name}
                </label>
              </div>
            ))
          )
        ) : que.id === 38 ? (
        <input
          type="date"
          className="form-control"
          value={answers[38] || ""}
          onChange={(e) => handleAnswerChange(38, e.target.value)}
        />
      ) : (
          <textarea
            className="form-control"
            placeholder="Enter your answer here"
            rows={3}
            value={answers[que.id] || ""}
            onChange={(e) => {
              // const cleanValue = e.target.value.replace(/[^a-zA-Z0-9 ]/g, ""); // allow only letters, numbers & spaces
              handleAnswerChange(que.id, e.target.value);
            }}
            pattern="^[A-Za-z0-9 ]*$"
          />
        )}

        {extra}
      </div>
    </>
  );

  const downloadRecording = async () => {
    if (!data?.id) return;
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/call-audits/${data.id}/download`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (!response.ok) {
        throw new Error("Failed to download recording");
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `call-recording-${data.agent_name || "agent"}.ogg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Cannot download recording: " + err.message);
    }
  };

  return (
    <>
      <div className="container py-1">
        <div className="welcom-main px-2 py-2 mb-3">
          <div className="">
            <div className="welcome" style={{ fontFamily: "Gilroy-Medium" }}>
              <span className="superAdmin">
                {" "}
                {auditType == "1"
                  ? "Transactional"
                  : auditType == "2"
                  ? "Transformational"
                  : auditType == "3"
                  ? "Hygiene"
                  : "Disposition"}{" "}
                Audit Review
              </span>
              <br />
              <span className="welcomeback">Listen, assess, and submit your QA feedback.</span>
            </div>
          </div>
        </div>
        <div className="row g-4">
          {/* Left Section */}
          <div className="col-lg-5 mt-3">
            <div className="card">
              <div className="card-body">
                {/* <h5 className="card-title mb-0">Call with {data?.agent_name}</h5> */}
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="card-title mb-0">
                    Call with {data?.agent_name}
                  </h5>
                  <i
                    className="bi bi-download"
                    title="Download recording"
                    onClick={downloadRecording}
                    style={{
                      cursor: "pointer",
                      fontSize: "20px",
                      color: "#2196f3",
                    }}
                  />
                </div>
                <p className="text-muted" style={{ fontSize: "14px" }}>
                  {" "}
                  {auditType == "1"
                    ? "Transactional"
                    : auditType == "2"
                    ? "Transformational"
                    : auditType == "3"
                    ? "Hygiene"
                    : "Disposition"}{" "}
                  Call
                </p>

                {/* Audio */}
                <div
                  style={{
                    width: "100%",
                    textAlign: "center",
                    paddingTop: "10px",
                    marginTop: "10px",
                    borderRadius: "12px",
                    border: "1px solid #421a1aff",
                  }}
                >
                  {/* Container for visualizer + progress bar */}
                  <div style={{ width: "80%", margin: "auto" }}>
                    {/* Visualizer */}
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        height: "30px",
                        alignItems: "flex-end",
                        marginBottom: "10px",
                      }}
                    >
                      {[...Array(30)].map((_, i) => (
                        <div
                          key={i}
                          style={{
                            flex: 1,
                            margin: "0 1px",
                            background: "#2196f3",
                            height: isPlaying ? `${10 + Math.random() * 20}px` : "4px",
                            transition: "height 0.2s",
                            borderRadius: "2px",
                          }}
                        />
                      ))}
                    </div>

                    {/* Progress bar */}
                    <div
                      onClick={handleProgressClick}
                      style={{
                        width: "100%",
                        height: "10px",
                        backgroundColor: "#ddd",
                        borderRadius: "5px",
                        cursor: "pointer",
                        position: "relative",
                      }}
                    >
                      <div
                        style={{
                          width: `${progress}%`,
                          height: "100%",
                          backgroundColor: "#2196f3",
                          borderRadius: "5px",
                          transition: "width 0.1s linear",
                        }}
                      />
                    </div>

                    {/* Time + Playback Speed */}
                    <div style={{ marginTop: "8px" }}>
                      <div className="d-flex justify-content-between align-items-center">
                        {/* Time */}
                        <div>
                          <span>{formatTime(currentTime)}</span> /{" "}
                          <span>{formatTime(duration)}</span>
                        </div>

                        {/* Playback speed button */}
                        <button
                          onClick={() => {
                            const speeds = [0.5, 0.75, 1, 1.25, 1.5, 2];
                            const currentIndex = speeds.indexOf(playbackRate);
                            const nextIndex = (currentIndex + 1) % speeds.length;
                            const newRate = speeds[nextIndex];

                            if (audioRef.current) audioRef.current.playbackRate = newRate;
                            setPlaybackRate(newRate);
                          }}
                          style={{
                            padding: "4px 10px",
                            borderRadius: "6px",
                            // border: "1px solid #2196f3",
                            border: "0px",
                            backgroundColor: "#f0f5ff",
                            color: "#2196f3",
                            fontSize: "13px",
                            cursor: "pointer",
                            minWidth: "50px",
                          }}
                        >
                          {playbackRate}x
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Controls */}
                  <div
                    style={{
                      fontSize: "32px",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      gap: "40px",
                    }}
                  >
                    <div
                      style={{ position: "relative", cursor: "pointer", display: "inline-block" }}
                      onClick={() => skipTime(-5)}
                      title="Rewind 5s"
                    >
                      <i
                        className="bi bi-arrow-counterclockwise"
                        style={{ color: "#5483B3", fontSize: "40px" }}
                      ></i>
                      <span
                        style={{
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                          fontSize: "12px",
                          color: "gray",
                        }}
                      >
                        -5
                      </span>
                    </div>

                    {isPlaying ? (
                      <i
                        className="bi bi-pause-circle-fill"
                        style={{ cursor: "pointer", color: "#2196f3", fontSize: "48px" }}
                        onClick={togglePlayback}
                      ></i>
                    ) : (
                      <i
                        className="bi bi-play-circle-fill"
                        style={{ cursor: "pointer", color: "#2196f3", fontSize: "48px" }}
                        onClick={togglePlayback}
                      ></i>
                    )}

                    <div
                      style={{ position: "relative", cursor: "pointer", display: "inline-block" }}
                      onClick={() => skipTime(5)}
                      title="Forward 5s"
                    >
                      <i
                        className="bi bi-arrow-clockwise"
                        style={{ color: "#5483B3", fontSize: "40px" }}
                      ></i>
                      <span
                        style={{
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                          fontSize: "12px",
                          color: "gray",
                        }}
                      >
                        +5
                      </span>
                    </div>
                  </div>

                  {/* Audio Element */}
                  <audio
                    ref={audioRef}
                    onTimeUpdate={handleTimeUpdate}
                    onLoadedMetadata={handleLoadedMetadata}
                    // src={playlist}
                    src={data?.recording_url}
                    preload="auto"
                    onError={() => {}}
                  />
                </div>

                <div className="call-info-css">
                  <h5 className="mx-3 mt-1 mb-1">Call Info</h5>
                  <p className="mx-3" style={{ fontSize: "14px" }}>
                    A quick overview to help guide your responses.
                  </p>
                  <ul className="list-group mt-2 list-group-flush">
                    <li className="list-group-item border d-flex justify-content-between">
                      <label>Agent Name</label>
                      <span style={{ fontSize: "14px" }}>{data?.agent_name || "-"}</span>
                    </li>

                    <li className="list-group-item mt-2 border d-flex justify-content-between">
                      <label>Disconnected By</label>
                      <span
                        className="text-truncate"
                        style={{
                          fontSize: "14px",
                          maxWidth: "150px",
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                        }}
                        title={data?.disconnect_by}
                      >
                        {data?.disconnect_by || "-"}
                      </span>
                    </li>

                    <li className="list-group-item mt-2 border d-flex justify-content-between">
                      <label>Audit Type</label>
                      <span style={{ fontSize: "14px" }}>
                        {auditType == "1"
                          ? "Transactional"
                          : auditType == "2"
                          ? "Transformational"
                          : auditType == "3"
                          ? "Hygiene"
                          : "Disposition"}
                      </span>
                    </li>

                    <li className="list-group-item mt-2 border d-flex justify-content-between">
                      <label>Call Date</label>
                      <span style={{ fontSize: "14px" }}>{formatDate(data?.start_time)}</span>
                    </li>

                    <li className="list-group-item mt-2 border d-flex justify-content-between">
                      <label>Disposition</label>
                      <span
                        className="text-truncate"
                        style={{
                          fontSize: "14px",
                          maxWidth: "150px",
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                        }}
                        title={data?.disposition_name}
                      >
                        {data?.disposition_name || "-"}
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Right Section */}
          <div className="col-lg-7 mt-3" style={{ overflow: "auto" }}>
            <div className="card" style={{ maxHeight: "86.5vh", overflowY: "auto" }}>
              <div className="card-body">
                <h5 className="">Hi {user_fname},</h5>
                <p>ready to review this call?</p>
                <p className="mt-2">
                  This one is a{" "}
                  {auditType == "1"
                    ? "Transactional"
                    : auditType == "2"
                    ? "Transformational"
                    : auditType == "3"
                    ? "Hygiene"
                    : "Disposition"}{" "}
                  QA – let’s evaluate how the agent went the extra mile for the customer. Your
                  feedback helps shape better experiences.
                </p>
                {auditType == "1" ? (
                  <div className="my-3 py-3 px-4 question-div-css">
                    <label
                      className={`form-label`}
                      style={{ color: "#5483B3", marginBottom: 0, fontSize: 16 }}
                    >
                      <strong>{currentCategory?.title}</strong>
                    </label>
                    {currentCategory?.title !== ztpTitle &&
                      currentCategory?.questions?.map((item, i) => (
                        <TabsBtn
                          alignKey={`question-${item.id}`}
                          title={
                            <label
                              className={`form-label ${
                                (responses[item?.id] == null ||
                                  responses[item?.id] === "" ||
                                  Number.isNaN(responses[item?.id])) &&
                                hasSubmit
                                  ? "text-danger fw-bold"
                                  : ""
                              }`}
                            >
                              {item.parameter}?
                            </label>
                          }
                          alignValue={responses[item.id] ?? null}
                          handleAlignChange={(value) => {
                            handleResponseChange(item.id, value);
                          }}
                          options={getOptions(item)}
                          disabled={
                            item.categoryId < actualCurrentCategoryIndex + 1 &&
                            responses[item.id] !== undefined
                          }
                        />
                      ))}
                    {currentCategory?.title === ztpTitle && (
                      <>
                        <select
                          className={`form-select text-truncate ${
                            hasSubmit && !selectedOption ? "text-danger fw-bold" : ""
                          }`}
                          style={{
                            maxWidth: "100%",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                          title={
                            currentCategory?.questions?.find((q) => q.id === selectedOption)
                              ?.parameter || ""
                          }
                          value={selectedOption}
                          onChange={(e) => setSelectedOption(e.target.value)}
                          required
                        >
                          <option value={""} disabled selected>
                            Select a parameter
                          </option>
                          {currentCategory?.questions?.map((item) => (
                            <option key={item.id} value={item.id} title={item.parameter}>
                              {item.parameter.length > 40
                                ? item.parameter.slice(0, 37) + "…" // truncate to 40 chars
                                : item.parameter}
                            </option>
                          ))}
                          <option value={"ztp-na"}>N/A</option>
                        </select>
                        <div className="my-2 mt-3">
                          <label className="form-label">Remarks</label>
                          <textarea
                            className="form-control"
                            rows={3}
                            value={remarks}
                            onChange={(e) => setRemarks(e.target.value)}
                          ></textarea>
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  <>
                    <div className="my-3 p-3 question-div-css" key={queations[0]?.id}>
                      {questionComponent(queations[0] || {})}
                    </div>

                    <div className="my-3 p-3 question-div-css" key={"category"}>
                      {questionComponent(
                        {
                          question:
                            "Select the appropriate disposition from the Disposition List that best fits the customer's issue.",
                          options: categories,
                          id: "category",
                        },
                        answers["category"] && dispositions.length > 0 ? (
                          <>
                            <div className="my-2 mt-4">
                              <label
                                className={`mb-2 ${
                                  !answers["disposition"] && hasSubmit ? "text-danger fw-bold" : ""
                                }`}
                              >
                                Choose a disposition that best describes the call outcome
                              </label>
                              <select
                                className="form-select"
                                value={answers.disposition || ""}
                                style={{ fontSize: "14px" }}
                                onChange={(e) =>
                                  handleAnswerChange("disposition", Number(e.target.value))
                                }
                              >
                                <option value="" disabled>
                                  Select a disposition
                                </option>
                                {dispositions.map((disp) => (
                                  <option key={disp.id} value={Number(disp.id)}>
                                    {disp.name}
                                  </option>
                                ))}
                              </select>
                            </div>
                            {answers["disposition"] && subDispositions.length > 0 && (
                              <div className="my-2 mt-4">
                                <label
                                  className={` ${
                                    !answers["subDisposition"] && hasSubmit
                                      ? "text-danger fw-bold"
                                      : ""
                                  }`}
                                >
                                  Choose a sub disposition that best describes the above call
                                  outcome
                                </label>
                                <select
                                  className="form-select"
                                  style={{ fontSize: "14px" }}
                                  value={answers.subDisposition || ""}
                                  onChange={(e) =>
                                    handleAnswerChange("subDisposition", Number(e.target.value))
                                  }
                                >
                                  <option value="" disabled>
                                    Select a disposition
                                  </option>
                                  {subDispositions.map((disp) => (
                                    <option key={disp.id} value={Number(disp.id)}>
                                      {disp.name}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            )}
                          </>
                        ) : null
                      )}
                    </div>

                    {queations?.slice(1)?.map((que, i) => (
                      <div className="my-3 p-3 question-div-css" key={que.id || i}>
                        {questionComponent(
                          que || {},
                          que.id === 9 && answers[9] === 1 ? (
                            <>
                              <textarea
                                className="form-control"
                                placeholder="Enter your answer here"
                                rows={3}
                                value={answers["fatalReason"] || ""}
                                onChange={(e) => handleAnswerChange("fatalReason", e.target.value)}
                              />
                            </>
                          ) : null
                        )}
                      </div>
                    ))}
                  </>
                )}
                <div className="px-4">
                  {auditType == "1" ? (
                    <div className="d-flex gap-4">
                      {currentCategoryIndex > 0 && (
                        <button
                          className="btn btn-primary my-2"
                          style={{ background: "#5483B3", width: "100%" }}
                          onClick={handlePrevQue}
                        >
                          Previous
                        </button>
                      )}
                      {currentCategoryIndex < categoryIds.length - 1 ? (
                        <>
                          <button
                            className="btn btn-primary my-2"
                            style={{ background: "#5483B3", width: "100%" }}
                            onClick={handleNextQue}
                          >
                            Next
                          </button>
                        </>
                      ) : (
                        <button
                          className="btn btn-primary my-2"
                          style={{ background: "#5483B3", width: "100%" }}
                          onClick={auditSubmit}
                        >
                          Submit
                        </button>
                      )}
                    </div>
                  ) : (
                    <button
                      className="btn btn-primary my-2"
                      style={{ background: "#5483B3", width: "100%" }}
                      onClick={auditSubmit}
                    >
                      Submit
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AuditDetails;
